import { Injectable } from '@angular/core';
import { Observable, catchError, forkJoin, map, of, switchMap, tap, throwError } from 'rxjs';
import { ApiService } from './api.service';
import { AppStateService } from './app-state.service';
import {
  AnswerSubmission,
  Category,
  CinephileRank,
  Question,
  QuestionOption,
  Quiz,
  QuizQuestionRef,
  StartAttemptResponse,
  SubmitAttemptResponse,
  UserQuizHistoryResponse
} from '../../models';

type AnyRecord = Record<string, any>;

@Injectable({ providedIn: 'root' })
export class QuizService {
  private readonly categoryPaths = [
    '/api/category/categories',
    '/api/categories',
  ];

  private readonly quizListPaths = [
    '/api/quiz/quizes',
    '/api/quiz/quizzes',
  ];

  constructor(
    private api: ApiService,
    private state: AppStateService
  ) {}

  // ── Categories ─────────────────────────────────────
  getCategories(): Observable<Category[]> {
    const requests = this.categoryPaths.map((path) => this.api.get<any>(path, { is_active: true }));

    return this.requestFirstSuccess(requests).pipe(
      map((raw) => this.readArray(raw, ['categories', 'data', 'results']).map((c) => this.normalizeCategory(c))),
      catchError(() => of([]))
    );
  }

  // ── Quizzes ────────────────────────────────────────
  getQuizzes(categoryId?: string): Observable<{ total: number; quizzes: Quiz[] }> {
    const params: Record<string, string | number | boolean> = { is_active: true, limit: 20, skip: 0 };
    if (categoryId) params['category_id'] = categoryId;

    const requests = this.quizListPaths.map((path) => this.api.get<any>(path, params));

    return this.requestFirstSuccess(requests).pipe(
      map((raw) => {
        const quizzes = this.readArray(raw, ['quizzes', 'quizes', 'data', 'results']).map((q) => this.normalizeQuiz(q));
        const total = this.readNumber(raw?.total ?? raw?.count ?? quizzes.length, quizzes.length);
        return { total, quizzes };
      }),
      catchError(() => of({ total: 0, quizzes: [] }))
    );
  }

  getDailyQuiz(): Observable<Quiz> {
    return this.getQuizzes().pipe(
      map(({ quizzes }) => quizzes[0] ?? this.emptyQuiz()),
      catchError(() => of(this.emptyQuiz()))
    );
  }

  getQuizWithQuestions(quizId: string): Observable<Quiz> {
    const requests = [
      `/api/quiz/get/${quizId}`,
      `/api/quiz/quizes/${quizId}`,
      `/api/quiz/quizzes/${quizId}`,
      `/api/quiz/${quizId}`
    ].map((path) => this.api.get<any>(path));

    return this.requestFirstSuccess(requests).pipe(
      map((raw) => this.normalizeQuiz(this.pickEntity(raw, ['quiz', 'data', 'result'], raw))),
      switchMap((quiz) => {
        const inlineQuestions = this.readArray((quiz as AnyRecord)?.['questions'], []).map((q) => this.normalizeQuestion(q));
        if (inlineQuestions.length) {
          return of({ ...quiz, questions: inlineQuestions });
        }

        const questionRefs = quiz.question_ids
          .map((q) => (typeof q === 'string' ? q : q?.question_id))
          .filter((id): id is string => !!id);

        if (!questionRefs.length) {
          return of({ ...quiz, questions: [] });
        }

        return forkJoin(
          questionRefs.map((questionId) =>
            this.requestFirstSuccess([
              this.api.get<any>(`/api/questions/${questionId}`),
              this.api.get<any>(`/api/question/questions/${questionId}`),
              this.api.get<any>(`/api/questions/get/${questionId}`)
            ]).pipe(
              map((rawQuestion) => this.normalizeQuestion(this.pickEntity(rawQuestion, ['question', 'data', 'result'], rawQuestion))),
              catchError(() => of(null))
            )
          )
        ).pipe(
          map((questions) => ({
            ...quiz,
            questions: questions.filter((q): q is Question => !!q)
          }))
        );
      })
    );
  }

  // ── Attempt lifecycle ──────────────────────────────
  startAttempt(quizId: string): Observable<StartAttemptResponse> {
    const payload = {
      quiz_id: quizId,
      user_id: this.state.userId
    };

    return this.requestFirstSuccess([
      this.api.post<any>('/api/quiz/attempts', payload),
      this.api.post<any>('/api/quiz/attempt/start', payload),
      this.api.post<any>('/api/quiz/attempts/start', payload)
    ])
      .pipe(
        map((raw) => {
          const attemptId = String(raw?.attempt_id ?? raw?._id ?? raw?.id ?? '');
          const startedAt = String(raw?.started_at ?? new Date().toISOString());
          return {
            attempt_id: attemptId,
            started_at: startedAt
          };
        }),
        tap((res) => {
          this.state.setAttemptId(res.attempt_id);
          this.state.setSelectedQuiz(quizId);
        })
      );
  }

  submitAttempt(answers: AnswerSubmission[], timeTaken?: number): Observable<SubmitAttemptResponse> {
    const attemptId = this.state.attemptId;
    if (!attemptId) throw new Error('No active attempt');

    const payload = { answers, time_taken: timeTaken ?? 0 };

    return this.api.post<any>(`/api/quiz/attempts/${attemptId}/submit`, payload).pipe(
      catchError(() => this.api.post<any>(`/api/quiz/attempts/${attemptId}/submit`, answers)),
      switchMap((raw) => {
        if (raw && typeof raw === 'object' && ('score' in raw || 'score_pct' in raw || 'passed' in raw)) {
          return of(this.normalizeSubmitResponse(raw, attemptId, timeTaken));
        }

        return this.api.get<any>(`/api/quiz/attempts/${attemptId}`).pipe(
          map((attemptRaw) => this.normalizeSubmitResponse(attemptRaw, attemptId, timeTaken)),
          catchError(() => of(this.normalizeSubmitResponse(raw ?? {}, attemptId, timeTaken)))
        );
      }),
      tap((result) => {
        this.state.setLastResult(result);
        if (result.tickets_earned > 0) {
          this.state.flashTickets(result.tickets_earned);
          const currentTickets = this.state.user?.tickets ?? 0;
          this.state.updateUserTickets(currentTickets + result.tickets_earned);
        }
      })
    );
  }

  // ── Start quiz flow (start attempt + load questions together) ──
  startQuizFlow(quizId: string): Observable<Quiz> {
    return this.startAttempt(quizId).pipe(switchMap(() => this.getQuizWithQuestions(quizId)));
  }

  getUserAttempts(userId: string, limit = 10): Observable<{ attempts: unknown[]; count: number }> {
    return this.api.get<any>(`/api/quiz/users/${userId}/attempts`, { skip: 0, limit }).pipe(
      map((raw) => {
        const attempts = this.readArray(raw, ['attempts', 'results', 'data']);
        const count = this.readNumber(raw?.count ?? raw?.total ?? attempts.length, attempts.length);
        return { attempts, count };
      }),
      catchError(() => of({ attempts: [], count: 0 }))
    );
  }

  getUserQuizHistory(userId: string, limit = 50): Observable<UserQuizHistoryResponse> {
    return this.api.get<any>(`/api/quiz/users/${userId}/history`, { skip: 0, limit }).pipe(
      catchError(() => this.api.get<any>(`/api/users/${userId}/quiz-history`, { skip: 0, limit })),
      map((raw) => {
        const rawHistory = this.readArray(raw?.history, []);
        const filteredHistory = rawHistory.filter((item: any) => {
          const hasAnswers = (item.answers && item.answers.length > 0) || (item.review_questions && item.review_questions.length > 0);
          const hasScores = (Number(item.correct_answers) > 0 || Number(item.wrong_answers) > 0 || Number(item.total_questions) > 0);
          return hasAnswers || hasScores;
        });
        const totalQuizzes = raw?.total_quizzes !== undefined && rawHistory.length === filteredHistory.length
          ? this.readNumber(raw?.total_quizzes, filteredHistory.length)
          : filteredHistory.length;

        return {
          user_id: String(raw?.user_id ?? userId),
          total_quizzes: totalQuizzes,
          total_correct: this.readNumber(raw?.total_correct, 0),
          total_wrong: this.readNumber(raw?.total_wrong, 0),
          total_questions: this.readNumber(raw?.total_questions, 0),
          overall_accuracy: this.readNumber(raw?.overall_accuracy, 0),
          best_category: raw?.best_category ?? null,
          category_breakdown: this.readArray(raw?.category_breakdown, []),
          history: filteredHistory,
        };
      }),
      catchError(() => of({
        user_id: userId,
        total_quizzes: 0,
        total_correct: 0,
        total_wrong: 0,
        total_questions: 0,
        overall_accuracy: 0,
        best_category: null,
        category_breakdown: [],
        history: []
      }))
    );
  }

  // ── Normalizers ────────────────────────────────────
  private normalizeCategory(raw: unknown): Category {
    const source = this.asRecord(this.pickEntity(raw, ['category', 'data', 'result'], raw));
    const id = String(source['id'] ?? source['_id'] ?? source['slug'] ?? '');
    return {
      id,
      _id: source['_id'] ? String(source['_id']) : undefined,
      name: String(source['name'] ?? 'Untitled Category'),
      slug: String(source['slug'] ?? id),
      description: source['description'] ? String(source['description']) : undefined,
      icon: source['icon'] ? String(source['icon']) : undefined,
      is_active: this.readBool(source['is_active'], true),
      question_count: this.readNumber(source['question_count'], 0),
      created_at: String(source['created_at'] ?? new Date().toISOString()),
      updated_at: source['updated_at'] ? String(source['updated_at']) : undefined
    };
  }

  private normalizeQuiz(raw: unknown): Quiz {
    const source = this.asRecord(this.pickEntity(raw, ['quiz', 'data', 'result'], raw));
    const id = String(source['id'] ?? source['_id'] ?? '');
    const questionIdsRaw = this.readArray(source['question_ids'], ['question_ids']);
    const inlineQuestionsRaw = this.readArray(source['questions'], ['questions']);
    const fallbackQuestionRefs = !questionIdsRaw.length
      ? inlineQuestionsRaw.map((question, index) => {
          const questionRecord = this.asRecord(question);
          return {
            question_id: String(questionRecord['question_id'] ?? questionRecord['id'] ?? questionRecord['_id'] ?? ''),
            order: this.readNumber(questionRecord['order'], index)
          };
        }).filter((question) => question.question_id.length > 0)
      : [];
    const questionIdSource = questionIdsRaw.length ? questionIdsRaw : fallbackQuestionRefs;
    const question_ids: Array<QuizQuestionRef | string> = questionIdsRaw.map((q: AnyRecord | string, index: number) => {
      if (typeof q === 'string') return q;
      const questionId = String(q?.['question_id'] ?? q?.['_id'] ?? q?.['id'] ?? '');
      return { question_id: questionId, order: this.readNumber(q?.['order'], index) };
    });

    return {
      id,
      _id: source['_id'] ? String(source['_id']) : undefined,
      title: String(source['title'] ?? 'Untitled Quiz'),
      description: source['description'] ? String(source['description']) : '',
      category_id: String(source['category_id'] ?? ''),
      time_limit: this.readNumber(source['time_limit'], 1),
      passing_score: this.readNumber(source['passing_score'], 70),
      is_active: this.readBool(source['is_active'], true),
      question_ids: questionIdSource.length
        ? questionIdSource.map((q: AnyRecord | string, index: number) => {
            if (typeof q === 'string') return q;
            const questionId = String(q?.['question_id'] ?? q?.['_id'] ?? q?.['id'] ?? '');
            return { question_id: questionId, order: this.readNumber(q?.['order'], index) };
          })
        : question_ids,
      questions: inlineQuestionsRaw.map((q) => this.normalizeQuestion(q)),
      created_at: String(source['created_at'] ?? new Date().toISOString()),
      updated_at: source['updated_at'] ? String(source['updated_at']) : undefined,
      image_url: source['image_url'] ? String(source['image_url']) : undefined,
      related_movie_id: source['related_movie_id'] ? this.readNumber(source['related_movie_id'], 0) : undefined
    };
  }

  private normalizeQuestion(raw: unknown): Question {
    const source = this.asRecord(this.pickEntity(raw, ['question', 'data', 'result'], raw));
    const id = String(source['id'] ?? source['_id'] ?? '');
    const options = this.readArray(source['options'], ['options']).map((opt: AnyRecord, idx: number) =>
      this.normalizeOption(opt, idx)
    );

    return {
      id,
      _id: source['_id'] ? String(source['_id']) : undefined,
      category_id: String(source['category_id'] ?? ''),
      question_text: String(source['question_text'] ?? ''),
      question_type: this.normalizeQuestionType(source['question_type']),
      difficulty: this.normalizeDifficulty(source['difficulty']),
      points: this.readNumber(source['points'], 1),
      time_limit: source['time_limit'] !== undefined ? this.readNumber(source['time_limit'], 60) : undefined,
      explanation: source['explanation'] ? String(source['explanation']) : undefined,
      is_active: this.readBool(source['is_active'], true),
      options,
      created_at: String(source['created_at'] ?? new Date().toISOString()),
      updated_at: source['updated_at'] ? String(source['updated_at']) : undefined
    };
  }

  private normalizeOption(raw: AnyRecord, fallbackIndex: number): QuestionOption {
    const numericOrder = this.readNumber(raw?.['order'], fallbackIndex);
    const isCorrect = typeof raw?.['is_correct'] === 'boolean'
      ? raw?.['is_correct']
      : typeof raw?.['is_correct'] === 'string'
        ? raw['is_correct'].toLowerCase() === 'true'
          ? true
          : raw['is_correct'].toLowerCase() === 'false'
            ? false
            : undefined
        : undefined;
    return {
      id: raw?.['id'] ?? raw?.['_id'] ?? raw?.['option_id'] ?? numericOrder,
      _id: raw?.['_id'] ? String(raw?.['_id']) : undefined,
      option_id: raw?.['option_id'] ? String(raw?.['option_id']) : undefined,
      option_text: String(raw?.['option_text'] ?? ''),
      is_correct: isCorrect,
      order: numericOrder
    };
  }

  private normalizeSubmitResponse(raw: AnyRecord, attemptId: string, timeTaken?: number): SubmitAttemptResponse {
    const scoreRaw = this.readNumber(raw?.['score_pct'] ?? raw?.['score_percent'] ?? raw?.['percentage'] ?? raw?.['score'], 0);
    const score_pct = scoreRaw > 1 ? Math.min(scoreRaw, 100) : Math.round(scoreRaw * 100);
    const questionResults = this.readArray(raw?.['question_results'], []);
    const totalQuestions = this.readNumber(
      raw?.['total_questions'] ?? raw?.['question_count'],
      questionResults.length || this.readArray(raw?.['answers'], []).length
    );
    const correctAnswers = this.readNumber(
      raw?.['correct_answers'] ?? raw?.['correct_count'],
      questionResults.filter((result) => this.asRecord(result)['is_correct'] === true).length
    );
    const wrongAnswers = totalQuestions > 0 ? Math.max(totalQuestions - correctAnswers, 0) : this.readNumber(raw?.['wrong_answers'], 0);
    
    const isPerfect = score_pct === 100 && totalQuestions > 0 && (correctAnswers === totalQuestions || correctAnswers > 0);
    const baseTickets = this.readNumber(raw?.['ticket_breakdown']?.base, correctAnswers * 10);
    const perfectBonus = this.readNumber(raw?.['ticket_breakdown']?.perfect_bonus, isPerfect ? 100 : 0);
    const dailyBonus = this.readNumber(raw?.['ticket_breakdown']?.daily_bonus, 0);
    const streakBonus = this.readNumber(raw?.['ticket_breakdown']?.streak_bonus, 0);

    let tickets = this.readNumber(raw?.['tickets_earned'] ?? raw?.['tickets'], 0);
    if (tickets <= 0 && correctAnswers > 0) {
      tickets = baseTickets + perfectBonus + dailyBonus + streakBonus;
    }

    const rank = this.resolveRankTitle(score_pct, raw?.['rank_title']);

    return {
      attempt_id: String(raw?.['attempt_id'] ?? raw?.['_id'] ?? attemptId),
      quiz_id: String(raw?.['quiz_id'] ?? this.state.selectedQuizId ?? ''),
      user_id: String(raw?.['user_id'] ?? this.state.userId),
      total_questions: totalQuestions,
      correct_answers: correctAnswers,
      wrong_answers: wrongAnswers,
      score_pct,
      points_earned: this.readNumber(raw?.['points_earned'] ?? raw?.['score'], score_pct),
      tickets_earned: tickets,
      ticket_breakdown: {
        base: baseTickets,
        perfect_bonus: perfectBonus,
        daily_bonus: dailyBonus,
        streak_bonus: streakBonus
      },
      rank_title: rank,
      passed: Boolean(raw?.['passed'] ?? score_pct >= this.readNumber(raw?.['passing_score'], 70)),
      time_taken: this.readNumber(raw?.['time_taken'], timeTaken ?? 0),
      streak_info: {
        new_streak: this.readNumber(raw?.['streak_info']?.new_streak, 0),
        extended: Boolean(raw?.['streak_info']?.extended),
        broken: Boolean(raw?.['streak_info']?.broken)
      },
      new_posters_unlocked: this.readArray(raw?.['new_posters_unlocked'], []),
      question_results: questionResults.map((result: AnyRecord) => ({
        question_id: String(result?.['question_id'] ?? ''),
        is_correct: Boolean(result?.['is_correct']),
        correct_ids: this.readArray(result?.['correct_ids'], []),
        selected_ids: this.readArray(result?.['selected_ids'], []),
        points_awarded: this.readNumber(result?.['points_awarded'], 0)
      })),
      answers: this.readArray(raw?.['answers'], [])
    };
  }

  private resolveRankTitle(scorePct: number, rawRank?: string): CinephileRank {
    const knownRanks: CinephileRank[] = ['The Extra', 'The Critic', 'The Cinephile', 'The Auteur', 'The Visionary'];
    if (rawRank && knownRanks.includes(rawRank as CinephileRank)) {
      return rawRank as CinephileRank;
    }
    if (scorePct >= 95) return 'The Visionary';
    if (scorePct >= 80) return 'The Auteur';
    if (scorePct >= 50) return 'The Cinephile';
    if (scorePct >= 20) return 'The Critic';
    return 'The Extra';
  }

  private normalizeQuestionType(rawType: unknown): Question['question_type'] {
    const normalized = String(rawType ?? 'single_choice').toLowerCase();
    if (normalized === 'multiple_choice' || normalized === 'true_false' || normalized === 'short_answer') {
      return normalized;
    }
    return 'single_choice';
  }

  private normalizeDifficulty(rawDifficulty: unknown): Question['difficulty'] {
    const normalized = String(rawDifficulty ?? 'medium').toLowerCase();
    if (normalized === 'easy' || normalized === 'hard') return normalized;
    return 'medium';
  }

  private requestFirstSuccess<T>(requests: Array<Observable<T>>, index = 0): Observable<T> {
    if (!requests.length) {
      return throwError(() => new Error('No request candidates configured.'));
    }

    if (index >= requests.length) {
      return throwError(() => new Error('All request candidates failed.'));
    }

    return requests[index].pipe(
      catchError((error) => {
        if (index >= requests.length - 1) {
          return throwError(() => error);
        }

        return this.requestFirstSuccess(requests, index + 1);
      })
    );
  }

  private pickEntity(raw: unknown, keys: string[], fallback: unknown): unknown {
    const parsed = this.parseMaybeString(raw);
    const root = this.asRecord(parsed);

    for (const key of keys) {
      const value = root[key];
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value;
      }
    }

    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed;
    }

    return fallback;
  }

  private parseMaybeString(raw: unknown): unknown {
    if (typeof raw !== 'string') return raw;

    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }

  private readArray(value: unknown, keys: string[] | []): any[] {
    const parsed = this.parseMaybeString(value);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === 'object') {
      for (const key of keys) {
        const candidate = (parsed as AnyRecord)[key];
        if (Array.isArray(candidate)) return candidate;
      }
    }
    return [];
  }

  private readBool(value: unknown, fallback: boolean): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      if (value.toLowerCase() === 'true') return true;
      if (value.toLowerCase() === 'false') return false;
    }

    return fallback;
  }

  private readNumber(value: unknown, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private asRecord(value: unknown): AnyRecord {
    return value && typeof value === 'object' ? (value as AnyRecord) : {};
  }

  private emptyQuiz(): Quiz {
    return {
      id: '',
      title: 'No active quizzes yet',
      description: 'Check back in a bit for new screenings.',
      category_id: '',
      time_limit: 1,
      passing_score: 70,
      is_active: true,
      question_ids: [],
      questions: [],
      created_at: new Date().toISOString()
    };
  }
}
